import { useEffect, useLayoutEffect, useRef } from 'react'
import { useCurrentCount, useWorkoutActions } from 'store/workout/selectors'
import { useIsPaused } from 'store/config/selectors'

interface Props {
  initCount: number
  cb: () => void
  enabled?: boolean
}

const HEARTBEAT_MS = 1000

const useCountdown = ({ initCount, cb, enabled = true }: Props) => {
  const { updateCurrentCount, decrementCurrentCount } = useWorkoutActions()
  const currentCount = useCurrentCount()
  const isPaused = useIsPaused()

  // Always point to latest values without forcing the interval effect to rerun
  const countRef = useRef(currentCount)
  const cbRef = useRef(cb)

  useEffect(() => {
    countRef.current = currentCount
  }, [currentCount])

  useEffect(() => {
    cbRef.current = cb
  }, [cb])

  // Initialize count only when enabled (optional but usually what you want)
useLayoutEffect(() => {
  if (!enabled) return
  if (countRef.current !== initCount) {
    countRef.current = initCount
    updateCurrentCount(initCount)
  }
}, [enabled, initCount, updateCurrentCount])

  useEffect(() => {
    if (!enabled || isPaused) return

    let intervalId: number | undefined

    intervalId = window.setInterval(() => {
      const count = countRef.current

      if (count > 1) {
        decrementCurrentCount()
      } else if (count === 1) {
        // Prevent repeated cb() calls if something hiccups
        if (intervalId !== undefined) {
          clearInterval(intervalId)
          intervalId = undefined
        }
        cbRef.current()
      }
    }, HEARTBEAT_MS)

    return () => {
      if (intervalId !== undefined) clearInterval(intervalId)
    }
  }, [enabled, isPaused, decrementCurrentCount])
}

export default useCountdown
